/*
 * Licensed to the Apache Software Foundation (ASF) under one or more
 * contributor license agreements.  See the NOTICE file distributed with
 * this work for additional information regarding copyright ownership.
 * The ASF licenses this file to You under the Apache License, Version 2.0
 * (the "License"); you may not use this file except in compliance with
 * the License.  You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import 'headroom.js';
import 'headroom.js/dist/angular.headroom';

import 'scrollmonitor/scrollMonitor.js';
import 'angular-viewport-watch/angular-viewport-watch.js';

import 'angular-ui-grid/ui-grid.css';
import 'angular-ui-grid';

import 'moment';
import 'moment-duration-format';

const requiredModules = [
  'ngCookies',
  'ngAnimate',
  'ngRoute',
  'ngSanitize',
  'angular-websocket',
  'ui.ace',
  'ui.bootstrap',
  'as.sortable',
  'ngTouch',
  'ngDragDrop',
  'angular.filter',
  'monospaced.elastic',
  'puElasticInput',
  'xeditable',
  'ngToast',
  'focus-if',
  'ngResource',
  'ngclipboard',
  'angularViewportWatch',
  'infinite-scroll',
  'ui.grid',
  'ui.grid.exporter',
  'ui.grid.selection',
  'ui.grid.emptyBaseLayer',
  'ui.grid.resizeColumns',
  'ui.grid.saveState',
  // SILECTIS
  'magpieDisplay',
  'jsonFormatter',
];

// headroom should not be used for CI, since we have to execute some integration tests.
// otherwise, they will fail.
if (!process.env.BUILD_CI) {
  requiredModules.push('headroom');
}

let zeppelinWebApp = angular.module('zeppelinWebApp', requiredModules)
  .filter('breakFilter', function() {
    return function(text) {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!text) {
        return text.replace(/\n/g, '<br />');
      }
    };
  })
  .config(function($httpProvider, $routeProvider, ngToastProvider) {
    // withCredentials when running locally via grunt
    $httpProvider.defaults.withCredentials = true;

    let visBundleLoad = {
      load: ['heliumService', function(heliumService) {
        return heliumService.load;
      }],
    };

    $routeProvider
      .when('/', {
        templateUrl: 'app/home/home.html',
      })
      .when('/account', {
        templateUrl: 'app/account/account.html',
        controller: 'AccountCtrl',
      })
      .when('/clusters', {
        templateUrl: 'app/clusters/cluster-manager.html',
        controller: 'ClusterManagerCtrl',
      })
      .when('/password-reset/:token', {
        templateUrl: 'app/password-reset/password-reset.html',
        controller: 'PasswordResetCtrl',
        publicAccess: true,
      })
      .when('/notebook/:noteId', {
        templateUrl: 'app/notebook/notebook.html',
        controller: 'NotebookCtrl',
        resolve: visBundleLoad,
      })
      .when('/notebook/:noteId/paragraph?=:paragraphId', {
        templateUrl: 'app/notebook/notebook.html',
        controller: 'NotebookCtrl',
        resolve: visBundleLoad,
      })
      .when('/notebook/:noteId/paragraph/:paragraphId?', {
        templateUrl: 'app/notebook/notebook.html',
        controller: 'NotebookCtrl',
        resolve: visBundleLoad,
      })
      .when('/notebook/:noteId/revision/:revisionId', {
        templateUrl: 'app/notebook/notebook.html',
        controller: 'NotebookCtrl',
        resolve: visBundleLoad,
      })
      .when('/interpreter', {
        templateUrl: 'app/interpreter/interpreter.html',
        controller: 'InterpreterCtrl',
        validateAccess: ($rootScope) => $rootScope.hasAnyGlobalRole(['SuperAdmin']),
      })
      .when('/revision-control', {
        templateUrl: 'app/notebook-repository/notebook-repository.html',
        controller: 'NotebookRepositoryCtrl',
        controllerAs: 'noterepo',
        validateAccess: function($rootScope) {
          return $rootScope.hasMagpieRole('OrganizationAdmin') ||
            $rootScope.hasGlobalRole('SuperAdmin');
        },
      })
      .when('/credential', {
        templateUrl: 'app/credential/credential.html',
        controller: 'CredentialCtrl',
        validateAccess: ($rootScope) => $rootScope.hasGlobalRole('SuperAdmin'),
      })
      .when('/helium', {
        templateUrl: 'app/helium/helium.html',
        controller: 'HeliumCtrl',
        validateAccess: ($rootScope) => $rootScope.hasGlobalRole('SuperAdmin'),
      })
      .when('/configuration', {
        templateUrl: 'app/configuration/configuration.html',
        controller: 'ConfigurationCtrl',
        validateAccess: ($rootScope) => $rootScope.hasGlobalRole('SuperAdmin'),
      })
      .when('/search/:searchTerm', {
        templateUrl: 'app/search/result-list.html',
        controller: 'SearchResultCtrl',
      })
      .when('/sso/error', {
        templateUrl: 'app/error/sso-error.html',
        publicAccess: true,
      })
      .otherwise({
        redirectTo: '/',
      });

    ngToastProvider.configure({
      dismissButton: true,
      dismissOnClick: false,
      combineDuplications: true,
      timeout: 6000,
    });
  })

  // handle logout on API failure
  .config(function($httpProvider, $provide) {
    if (process.env.PROD) {
      $httpProvider.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
    }
    $provide.factory('httpInterceptor', function($q, $rootScope) {
      return {
        'responseError': function(rejection) {
          if (rejection.status === 405) {
            let data = {};
            data.info = '';
            $rootScope.$broadcast('session_logout', data);
          }
          $rootScope.$broadcast('httpResponseError', rejection);
          return $q.reject(rejection);
        },
      };
    });
    $httpProvider.interceptors.push('httpInterceptor');
  })
  .constant('TRASH_FOLDER_ID', '~Trash');

function auth() {
  let $http = angular.injector(['ng']).get('$http');
  let baseUrlSrv = angular.injector(['zeppelinWebApp']).get('baseUrlSrv');
  angular.injector(['zeppelinWebApp']).get('loaderSrv').hideLoader();
  // withCredentials when running locally via grunt
  $http.defaults.withCredentials = true;
  jQuery.ajaxSetup({
    dataType: 'json',
    xhrFields: {
      withCredentials: true,
    },
    crossDomain: true,
  });
  let config = (process.env.PROD) ? {headers: {'X-Requested-With': 'XMLHttpRequest'}} : {};
  return $http.get(baseUrlSrv.getRestApiBase() + '/security/ticket', config).then(function(response) {
    zeppelinWebApp.run(function($rootScope) {
      let res = angular.fromJson(response.data).body;
      if (res['redirectURL']) {
        window.location.href = res['redirectURL'] + window.location.href;
      } else {
        $rootScope.ticket = res;
        $rootScope.ticket.screenUsername = $rootScope.ticket.principal;
        if ($rootScope.ticket.principal.indexOf('#Pac4j') === 0) {
          let re = ', name=(.*?),';
          $rootScope.ticket.screenUsername = $rootScope.ticket.principal.match(re)[1];
        }
      }
    });
  }, function(errorResponse) {
    // Handle error case
    let redirect = errorResponse.headers('Location');
    if (errorResponse.status === 401 && redirect !== undefined && redirect !== null) {
      // Handle page redirect
      window.location.href = redirect;
    }
  });
}

function bootstrapApplication() {
  zeppelinWebApp.run(function($rootScope, $location, $http, baseUrlSrv) {
    // get magpie config
    $rootScope.magpieConfig = {};
    $http.get(baseUrlSrv.getRestApiBase() + '/magpie/config')
      .then(function(response) {
        $rootScope.magpieConfig = response.data;
        $rootScope.$broadcast('magpieConfig');
      });

    // SILECTIS -> detecting whether the user has a role
    $rootScope.hasRole = (role) => {
      if (!$rootScope.parsedRoles) {
        if ($rootScope.ticket && $rootScope.ticket.roles) {
          $rootScope.parsedRoles = JSON.parse($rootScope.ticket.roles);
        }
      }

      if ($rootScope.parsedRoles) {
        const roles = JSON.parse($rootScope.ticket.roles);
        return roles.indexOf(role) > -1;
      } else {
        return false;
      }
    };

    $rootScope.hasGlobalRole = (role) => $rootScope.hasRole(`global.${role}`);
    $rootScope.hasMagpieRole = (role) => $rootScope.hasRole(`magpie.${role}`);

    $rootScope.hasAnyRole = (roles) => _.some(roles, $rootScope.hasRole);
    $rootScope.hasAnyGlobalRole = (roles) => _.some(roles, $rootScope.hasGlobalRole);
    $rootScope.hasAnyMagpieRole = (roles) => _.some(roles, $rootScope.hasMagpieRole);

    $rootScope.$on('$routeChangeStart', function(event, next, current) {
      $rootScope.pageTitle = 'Silectis | Magpie';
      if (next.$$route && (next.$$route.originalPath === '/' || next.$$route.redirectTo === '/')) {
        /* Do not do anything.  This use case is when the user puts the root url in the browser
           ie. magpie.silect.is and it gets redirected to magpie.silect.is/#/.  Two calls will be made
           to this $on function for that scenario.  The first call will be for the redirectTo '/' value
           and the second will be the originalPath value of '/'.  We want to have this step
           so that subsequent else if statement are easier to read. */
      } else if (!$rootScope.ticket && next.$$route && !next.$$route.publicAccess) {
        /* Here we want to rebuild the url as a ref query param so that we can redirect
           back to the page after the user successfully logins. */
        let ref = next.$$route.originalPath
          .split('/')
          .map(function(p) {
            if(!next.params[p.replace(':', '')]) {
              return p;
            } else {
              return next.params[p.replace(':', '')];
            }
          })
          .join('/');

        $location.replaceState('/', 'ref='+ ref);
      } else if (next.$$route && next.$$route.validateAccess && !next.$$route.validateAccess($rootScope)) {
        // has an access validation function and the function returned false
        $location.path('/');
      }
    });
  });
  angular.bootstrap(document, ['zeppelinWebApp']);
}

angular.element(document).ready(function() {
  auth().then(bootstrapApplication);
});



// WEBPACK FOOTER //
// ./src/app/app.js